/**
 * プロジェクト差分表示タブ
 *
 * 現在開いているプロジェクトと、選択したMDSファイルの差分を表示する。
 */

import React, { useState, useCallback, useRef, useMemo } from 'react';
import * as styles from './ProjectDiffTabContent.css';
import type { TabState, DatasetId, ColumnSchema, MIDASProject, PrimaryDataSet, DataSet } from '../../../../types';
import { useProjectStore } from '../../../stores/projectStore';
import { deserializeMDSFile } from '../../../services/storageService';
import {
  diffProject,
  type ProjectDiff,
  type PrimaryDataSetDiff,
  type ReportDiff,
  type ContentLineDiff,
} from '../../../utils/diff';
import { ROW_ID_COLUMN_ID } from '../../../../types/system-columns';
import { createDataTableTab, createReportTab } from '../../../tab-system/TabRegistry';
import type { ReportId } from '../../../../types/id.types';
import logger from '../../../utils/logger';

interface ProjectDiffTabContentProps {
  tab: TabState;
}

// 比較の方向
// 'file-as-original': 選択したMDSファイルを変更前(Original)、現在のプロジェクトを変更後(Modified)として比較
// 'current-as-original': 現在のプロジェクトを変更前(Original)、選択したMDSファイルを変更後(Modified)として比較
type CompareDirection = 'file-as-original' | 'current-as-original';

export const ProjectDiffTabContent: React.FC<ProjectDiffTabContentProps> = () => {
  const [comparedProject, setComparedProject] = useState<MIDASProject | null>(null);
  const [comparedFileName, setComparedFileName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [expandedDatasets, setExpandedDatasets] = useState<Set<string>>(new Set());
  const [expandedReports, setExpandedReports] = useState<Set<string>>(new Set());
  const [compareDirection, setCompareDirection] = useState<CompareDirection>('file-as-original');
  const project = useProjectStore((state) => state.project);
  const addTab = useProjectStore((state) => state.addTab);
  const activeContainerId = useProjectStore((state) => {
    if (!state.project?.activeWorkspaceId) return null;
    const workspace = state.project.workspaces.find(w => w.id === state.project?.activeWorkspaceId);
    return workspace?.layoutState.activeContainerId ?? null;
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 差分を計算（compareDirectionまたはプロジェクトが変わったら再計算）
  const diffResult = useMemo(() => {
    if (!project || !comparedProject) return null;

    const [originalProject, modifiedProject] = compareDirection === 'file-as-original'
      ? [comparedProject, project]
      : [project, comparedProject];

    const diff = diffProject(originalProject, modifiedProject);

    logger.info('[ProjectDiffTabContent] Diff calculated', {
      fileName: comparedFileName,
      compareDirection,
      hasDifferences: diff.hasDifferences,
      primaryDatasetsAdded: diff.primaryDatasets.added.length,
      primaryDatasetsRemoved: diff.primaryDatasets.removed.length,
      primaryDatasetsModified: Object.keys(diff.primaryDatasets.modified).length,
    });

    return { diff, originalProject, modifiedProject };
  }, [project, comparedProject, compareDirection, comparedFileName]);

  // ファイル選択ボタンクリック時
  const handleSelectFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // ファイルが選択された時のハンドラー
  const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // input をリセット（同じファイルを再選択可能にする）
    event.target.value = '';

    if (!project) {
      setErrorMessage('No project is currently open.');
      return;
    }

    try {
      setIsLoading(true);
      setErrorMessage(null);

      const arrayBuffer = await file.arrayBuffer();
      const data = new Uint8Array(arrayBuffer);

      // MDSファイルをデシリアライズ
      const loadedProject = await deserializeMDSFile(data);

      setComparedProject(loadedProject);
      setComparedFileName(file.name);
    } catch (error) {
      logger.error('[ProjectDiffTabContent] Failed to load file', error as Error);
      setErrorMessage(error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [project]);

  // データセット展開/折りたたみ
  const toggleDatasetExpand = useCallback((datasetId: string) => {
    setExpandedDatasets((prev) => {
      const next = new Set(prev);
      if (next.has(datasetId)) {
        next.delete(datasetId);
      } else {
        next.add(datasetId);
      }
      return next;
    });
  }, []);

  // データセット名を取得（現在のプロジェクトから優先、なければ比較対象から）
  const getDatasetName = useCallback((datasetId: string): string | undefined => {
    const currentDataset = project?.datasets[datasetId as DatasetId];
    if (currentDataset) return currentDataset.name;
    const comparedDataset = comparedProject?.datasets[datasetId as DatasetId];
    if (comparedDataset) return comparedDataset.name;
    return undefined;
  }, [project, comparedProject]);

  // データセットタブを開く
  const openDatasetTab = useCallback((datasetId: string) => {
    // 現在のプロジェクトにあるデータセットのみ開ける
    const dataset = project?.datasets[datasetId as DatasetId];
    if (!dataset) {
      logger.warn('[ProjectDiffTabContent] Cannot open tab: dataset not in current project', { datasetId });
      return;
    }
    if (!activeContainerId) {
      logger.warn('[ProjectDiffTabContent] Cannot open tab: no active container');
      return;
    }
    const tab = createDataTableTab(datasetId as DatasetId, dataset.name);
    addTab(activeContainerId, tab);
  }, [project, addTab, activeContainerId]);

  // レポートタブを開く
  const openReportTab = useCallback((reportId: string) => {
    // 現在のプロジェクトにあるレポートのみ開ける
    const report = project?.reports?.find(r => r.id === reportId);
    if (!report) {
      logger.warn('[ProjectDiffTabContent] Cannot open tab: report not in current project', { reportId });
      return;
    }
    if (!activeContainerId) {
      logger.warn('[ProjectDiffTabContent] Cannot open tab: no active container');
      return;
    }
    const tab = createReportTab(reportId as ReportId, report.name);
    addTab(activeContainerId, tab);
  }, [project, addTab, activeContainerId]);

  // データセットIDリストをレンダリング（名前付きリンク）
  const renderDatasetIdList = (label: string, ids: string[]) => {
    if (ids.length === 0) return null;
    return (
      <div className={styles.idList}>
        {label}:{' '}
        {ids.map((id, index) => {
          const name = getDatasetName(id);
          const isInCurrentProject = project?.datasets[id as DatasetId] !== undefined;
          const displayText = name ? `${name}` : id;
          return (
            <span key={id}>
              {index > 0 && ', '}
              {isInCurrentProject ? (
                <button
                  className={styles.datasetLink}
                  onClick={() => openDatasetTab(id)}
                  title={`Open ${displayText}`}
                >
                  {displayText}
                </button>
              ) : (
                <span title={`Not in current project: ${id}`}>{displayText}</span>
              )}
            </span>
          );
        })}
      </div>
    );
  };

  // 汎用IDリストをレンダリング（リンクなし）
  const renderIdList = (label: string, ids: string[]) => {
    if (ids.length === 0) return null;
    return (
      <div className={styles.idList}>
        {label}: {ids.join(', ')}
      </div>
    );
  };

  // レポートIDリストをレンダリング（名前付き）
  const renderReportIdList = (label: string, reportIds: string[], originalProject: MIDASProject, modifiedProject: MIDASProject) => {
    if (reportIds.length === 0) return null;

    // 両方のプロジェクトからレポート名を取得できるようにする
    const getReportName = (id: string): string | undefined => {
      const report = modifiedProject.reports?.find(r => r.id === id) ||
                     originalProject.reports?.find(r => r.id === id);
      return report?.name;
    };

    return (
      <div className={styles.idList}>
        {label}: {reportIds.map((id, i) => {
          const name = getReportName(id);
          return (
            <span key={id}>
              {i > 0 && ', '}
              <span title={id}>{name ? `${name}` : id}</span>
            </span>
          );
        })}
      </div>
    );
  };

  // 差分サマリーをレンダリング
  const renderDiffSummary = (diff: ProjectDiff, originalProject: MIDASProject, modifiedProject: MIDASProject) => {
    const primaryModifiedCount = Object.keys(diff.primaryDatasets.modified).length;
    const reportsModifiedCount = Object.keys(diff.reports.modified).length;

    return (
      <div className={styles.summaryList}>
        {/* PrimaryDataSet */}
        <div className={styles.summarySection}>
          <div className={styles.summarySectionTitle}>Primary Datasets</div>
          <div className={styles.summaryRow}>
            +{diff.primaryDatasets.added.length} added, -{diff.primaryDatasets.removed.length} removed, {primaryModifiedCount} modified
          </div>
          {renderDatasetIdList('Added', diff.primaryDatasets.added)}
          {renderDatasetIdList('Removed', diff.primaryDatasets.removed)}
          {renderDatasetIdList('Modified', Object.keys(diff.primaryDatasets.modified))}
        </div>

        {/* DerivedDataSet */}
        <div className={styles.summarySection}>
          <div className={styles.summarySectionTitle}>Derived Datasets</div>
          <div className={styles.summaryRow}>
            +{diff.derivedDatasets.added.length} added, -{diff.derivedDatasets.removed.length} removed, {diff.derivedDatasets.modified.length} modified
          </div>
          {renderDatasetIdList('Added', diff.derivedDatasets.added)}
          {renderDatasetIdList('Removed', diff.derivedDatasets.removed)}
          {renderDatasetIdList('Modified', diff.derivedDatasets.modified)}
        </div>

        {/* Models */}
        <div className={styles.summarySection}>
          <div className={styles.summarySectionTitle}>Models</div>
          <div className={styles.summaryRow}>
            +{diff.models.added.length} added, -{diff.models.removed.length} removed, {diff.models.modified.length} modified
          </div>
          {renderIdList('Added', diff.models.added)}
          {renderIdList('Removed', diff.models.removed)}
          {renderIdList('Modified', diff.models.modified)}
        </div>

        {/* Reports */}
        <div className={styles.summarySection}>
          <div className={styles.summarySectionTitle}>Reports</div>
          <div className={styles.summaryRow}>
            +{diff.reports.added.length} added, -{diff.reports.removed.length} removed, {reportsModifiedCount} modified
          </div>
          {renderReportIdList('Added', diff.reports.added, originalProject, modifiedProject)}
          {renderReportIdList('Removed', diff.reports.removed, originalProject, modifiedProject)}
          {renderReportIdList('Modified', Object.keys(diff.reports.modified), originalProject, modifiedProject)}
        </div>
      </div>
    );
  };

  // 指定された行のデータを取得
  const getRowData = (dataset: PrimaryDataSet, rowNumber: number): Record<string, unknown> => {
    const rowIdColumn = dataset.data[ROW_ID_COLUMN_ID];
    if (!rowIdColumn) return {};

    // rowNumberに対応するインデックスを見つける
    const rowIndex = rowIdColumn.findIndex((v) => v === rowNumber);
    if (rowIndex === -1) return {};

    // 各列のデータを取得
    const rowData: Record<string, unknown> = {};
    for (const col of dataset.schema.columns) {
      // システム列はスキップ
      if (col.id === ROW_ID_COLUMN_ID) continue;
      const values = dataset.data[col.id];
      if (values) {
        rowData[col.name] = values[rowIndex];
      }
    }
    return rowData;
  };

  // PrimaryDataSetかどうかをチェックする型ガード
  const isPrimaryDataSet = (ds: DataSet | undefined): ds is PrimaryDataSet => {
    return ds !== undefined && ds.type === 'primary';
  };

  // データセット詳細をレンダリング
  const renderDatasetDetails = (datasetId: string, datasetDiff: PrimaryDataSetDiff, originalProject: MIDASProject, modifiedProject: MIDASProject) => {
    const isExpanded = expandedDatasets.has(datasetId);
    const datasetRaw = modifiedProject.datasets[datasetId as DatasetId];
    const originalDatasetRaw = originalProject.datasets[datasetId as DatasetId];
    const dataset = isPrimaryDataSet(datasetRaw) ? datasetRaw : undefined;
    const originalDataset = isPrimaryDataSet(originalDatasetRaw) ? originalDatasetRaw : undefined;
    const datasetName = dataset?.name || originalDataset?.name || datasetId;

    // 変更サマリー
    const changes: string[] = [];
    if (datasetDiff.nameChanged) {
      changes.push('name changed');
    }
    if (datasetDiff.rowsAdded.length > 0) {
      changes.push(`${datasetDiff.rowsAdded.length} row${datasetDiff.rowsAdded.length > 1 ? 's' : ''} added`);
    }
    if (datasetDiff.rowsDeleted.length > 0) {
      changes.push(`${datasetDiff.rowsDeleted.length} row${datasetDiff.rowsDeleted.length > 1 ? 's' : ''} deleted`);
    }
    if (datasetDiff.exclusionChanges.length > 0) {
      const excluded = datasetDiff.exclusionChanges.filter(c => c.type === 'excluded').length;
      const unexcluded = datasetDiff.exclusionChanges.filter(c => c.type === 'unexcluded').length;
      if (excluded > 0) changes.push(`${excluded} row${excluded > 1 ? 's' : ''} excluded`);
      if (unexcluded > 0) changes.push(`${unexcluded} row${unexcluded > 1 ? 's' : ''} unexcluded`);
    }
    if (datasetDiff.cellChanges.length > 0) {
      changes.push(`${datasetDiff.cellChanges.length} cell${datasetDiff.cellChanges.length > 1 ? 's' : ''} changed`);
    }
    if (datasetDiff.schemaChanges.columnChanges.length > 0) {
      changes.push(`${datasetDiff.schemaChanges.columnChanges.length} schema change${datasetDiff.schemaChanges.columnChanges.length > 1 ? 's' : ''}`);
    }
    if (datasetDiff.metadataChanges.length > 0) {
      changes.push(`${datasetDiff.metadataChanges.length} metadata change${datasetDiff.metadataChanges.length > 1 ? 's' : ''}`);
    }
    if (datasetDiff.commentChanges.length > 0) {
      changes.push(`${datasetDiff.commentChanges.length} comment change${datasetDiff.commentChanges.length > 1 ? 's' : ''}`);
    }

    return (
      <div key={datasetId} className={styles.datasetItem}>
        <button
          className={styles.datasetHeader}
          onClick={() => toggleDatasetExpand(datasetId)}
          aria-expanded={isExpanded}
          data-testid={`dataset-expand-${datasetId}`}
        >
          <span className={styles.expandIcon}>{isExpanded ? '▼' : '▶'}</span>
          <span className={styles.datasetName}>{datasetName}</span>
          <span className={styles.changeSummary}>{changes.join(', ')}</span>
        </button>
        {isExpanded && (
          <div className={styles.datasetDetails}>
            {/* セル変更 */}
            {datasetDiff.cellChanges.length > 0 && (
              <div className={styles.detailSection}>
                <h4 className={styles.detailTitle}>Cell Changes</h4>
                <table className={styles.changeTable}>
                  <thead>
                    <tr>
                      <th className={styles.changeTableHeader}>Row</th>
                      <th className={styles.changeTableHeader}>Column</th>
                      <th className={styles.changeTableHeader}>Old Value</th>
                      <th className={styles.changeTableHeader}>New Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datasetDiff.cellChanges.slice(0, 100).map((change, idx) => (
                      <tr key={idx}>
                        <td className={styles.changeTableCellNumeric}>{change.rowNumber}</td>
                        <td className={styles.changeTableCell}>{getColumnName(datasetId, change.columnId)}</td>
                        <td className={getValueCellStyle(change.oldValue, styles.oldValue)}>{formatValue(change.oldValue)}</td>
                        <td className={getValueCellStyle(change.newValue, styles.newValue)}>{formatValue(change.newValue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {datasetDiff.cellChanges.length > 100 && (
                  <div className={styles.truncatedNote}>
                    ... and {datasetDiff.cellChanges.length - 100} more changes
                  </div>
                )}
                {datasetDiff._truncated && (
                  <div className={styles.truncatedNote}>
                    (Total: {datasetDiff._truncatedCellChanges} changes, showing first 100)
                  </div>
                )}
              </div>
            )}

            {/* 行追加 */}
            {datasetDiff.rowsAdded.length > 0 && dataset && (
              <div className={styles.detailSection}>
                <h4 className={styles.detailTitle}>Rows Added</h4>
                {(() => {
                  const columns = dataset.schema.columns.filter(c => c.id !== ROW_ID_COLUMN_ID);
                  const rowsToShow = datasetDiff.rowsAdded.slice(0, 20);
                  return (
                    <>
                      <table className={styles.changeTable}>
                        <thead>
                          <tr>
                            <th className={styles.changeTableHeader}>Row</th>
                            {columns.map(col => (
                              <th key={col.id} className={styles.changeTableHeader}>{col.name}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {rowsToShow.map(rowNumber => {
                            const rowData = getRowData(dataset, rowNumber);
                            return (
                              <tr key={rowNumber}>
                                <td className={styles.changeTableCellNumeric}>{rowNumber}</td>
                                {columns.map(col => (
                                  <td
                                    key={col.id}
                                    className={`${getValueCellStyle(rowData[col.name], '')} ${styles.newValue}`}
                                  >
                                    {formatValue(rowData[col.name])}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {datasetDiff.rowsAdded.length > 20 && (
                        <div className={styles.truncatedNote}>
                          ... and {datasetDiff.rowsAdded.length - 20} more rows
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            {/* 行削除 */}
            {datasetDiff.rowsDeleted.length > 0 && originalDataset && (
              <div className={styles.detailSection}>
                <h4 className={styles.detailTitle}>Rows Deleted</h4>
                {(() => {
                  const columns = originalDataset.schema.columns.filter(c => c.id !== ROW_ID_COLUMN_ID);
                  const rowsToShow = datasetDiff.rowsDeleted.slice(0, 20);
                  return (
                    <>
                      <table className={styles.changeTable}>
                        <thead>
                          <tr>
                            <th className={styles.changeTableHeader}>Row</th>
                            {columns.map(col => (
                              <th key={col.id} className={styles.changeTableHeader}>{col.name}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {rowsToShow.map(rowNumber => {
                            const rowData = getRowData(originalDataset, rowNumber);
                            return (
                              <tr key={rowNumber}>
                                <td className={styles.changeTableCellNumeric}>{rowNumber}</td>
                                {columns.map(col => (
                                  <td
                                    key={col.id}
                                    className={`${getValueCellStyle(rowData[col.name], '')} ${styles.oldValue}`}
                                  >
                                    {formatValue(rowData[col.name])}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {datasetDiff.rowsDeleted.length > 20 && (
                        <div className={styles.truncatedNote}>
                          ... and {datasetDiff.rowsDeleted.length - 20} more rows
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            {/* スキーマ変更 */}
            {datasetDiff.schemaChanges.columnChanges.length > 0 && (
              <div className={styles.detailSection}>
                <h4 className={styles.detailTitle}>Schema Changes</h4>
                <ul className={styles.schemaList}>
                  {datasetDiff.schemaChanges.columnChanges.map((change, idx) => (
                    <li key={idx}>
                      {change.type === 'added' && (
                        <span className={styles.schemaAdded}>+ Column "{change.newName}" added</span>
                      )}
                      {change.type === 'removed' && (
                        <span className={styles.schemaRemoved}>- Column "{change.oldName}" removed</span>
                      )}
                      {change.type === 'renamed' && (
                        <span className={styles.schemaModified}>
                          Column renamed: "{change.oldName}" → "{change.newName}"
                        </span>
                      )}
                      {change.type === 'typeChanged' && (
                        <span className={styles.schemaModified}>
                          Column type changed: {change.oldType} → {change.newType}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* 名前変更 */}
            {datasetDiff.nameChanged && (
              <div className={styles.detailSection}>
                <h4 className={styles.detailTitle}>Name Changed</h4>
                <div className={styles.rowList}>
                  "{datasetDiff.oldName}" → "{datasetDiff.newName}"
                </div>
              </div>
            )}

            {/* 除外行の変更 */}
            {datasetDiff.exclusionChanges.length > 0 && (
              <div className={styles.detailSection}>
                <h4 className={styles.detailTitle}>Exclusion Changes</h4>
                <ul className={styles.schemaList}>
                  {datasetDiff.exclusionChanges.map((change, idx) => (
                    <li key={idx}>
                      {change.type === 'excluded' ? (
                        <span className={styles.schemaRemoved}>Row {change.rowNumber} excluded</span>
                      ) : (
                        <span className={styles.schemaAdded}>Row {change.rowNumber} unexcluded</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* メタデータ変更 */}
            {datasetDiff.metadataChanges.length > 0 && (
              <div className={styles.detailSection}>
                <h4 className={styles.detailTitle}>Metadata Changes</h4>
                <ul className={styles.schemaList}>
                  {datasetDiff.metadataChanges.map((change, idx) => (
                    <li key={idx}>
                      <span className={styles.schemaModified}>
                        {change.field}: "{change.oldValue || '(empty)'}" → "{change.newValue || '(empty)'}"
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* コメント変更 */}
            {datasetDiff.commentChanges.length > 0 && (
              <div className={styles.detailSection}>
                <h4 className={styles.detailTitle}>Comment Changes</h4>
                <ul className={styles.schemaList}>
                  {datasetDiff.commentChanges.map((change, idx) => (
                    <li key={idx}>
                      {change.type === 'added' && (
                        <span className={styles.schemaAdded}>
                          Row {change.rowNumber}: + "{change.newComment}"
                        </span>
                      )}
                      {change.type === 'removed' && (
                        <span className={styles.schemaRemoved}>
                          Row {change.rowNumber}: - "{change.oldComment}"
                        </span>
                      )}
                      {change.type === 'modified' && (
                        <span className={styles.schemaModified}>
                          Row {change.rowNumber}: "{change.oldComment}" → "{change.newComment}"
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // レポート詳細をレンダリング
  const renderReportDetails = (reportId: string, reportDiff: ReportDiff) => {
    const isExpanded = expandedReports.has(reportId);

    // 変更サマリー
    const changes: string[] = [];
    if (reportDiff.nameChanged) {
      changes.push('name changed');
    }
    if (reportDiff.descriptionChanged) {
      changes.push('description changed');
    }
    if (reportDiff.contentDiff.hasChanges) {
      changes.push(`content changed (+${reportDiff.contentDiff.addedLines}/-${reportDiff.contentDiff.removedLines} lines)`);
    }
    const addedElements = reportDiff.elementChanges.filter(e => e.type === 'added').length;
    const removedElements = reportDiff.elementChanges.filter(e => e.type === 'removed').length;
    const modifiedElements = reportDiff.elementChanges.filter(e => e.type === 'modified').length;
    if (addedElements > 0) {
      changes.push(`${addedElements} element${addedElements > 1 ? 's' : ''} added`);
    }
    if (removedElements > 0) {
      changes.push(`${removedElements} element${removedElements > 1 ? 's' : ''} removed`);
    }
    if (modifiedElements > 0) {
      changes.push(`${modifiedElements} element${modifiedElements > 1 ? 's' : ''} modified`);
    }
    if (reportDiff.settingsChanged) {
      changes.push('settings changed');
    }

    const toggleExpand = () => {
      setExpandedReports(prev => {
        const next = new Set(prev);
        if (next.has(reportId)) {
          next.delete(reportId);
        } else {
          next.add(reportId);
        }
        return next;
      });
    };

    // レポートが現在のプロジェクトにあるかチェック
    const isReportInCurrentProject = project?.reports?.some(r => r.id === reportId) ?? false;

    return (
      <div key={reportId} className={styles.datasetItem}>
        <div className={styles.datasetHeader}>
          <button
            className={styles.expandIcon}
            onClick={toggleExpand}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? '▼' : '▶'}
          </button>
          {isReportInCurrentProject ? (
            <button
              className={styles.datasetLink}
              onClick={() => openReportTab(reportId)}
              title={`Open ${reportDiff.reportName}`}
            >
              {reportDiff.reportName}
            </button>
          ) : (
            <span className={styles.datasetName}>{reportDiff.reportName}</span>
          )}
          <span className={styles.changeSummary}>({changes.join(', ')})</span>
        </div>

        {isExpanded && (
          <div className={styles.datasetDetails}>
            {/* 名前変更 */}
            {reportDiff.nameChanged && (
              <div className={styles.detailSection}>
                <h4 className={styles.detailTitle}>Name Changed</h4>
                <div className={styles.rowList}>
                  "{reportDiff.oldName}" → "{reportDiff.newName}"
                </div>
              </div>
            )}

            {/* 説明の変更 */}
            {reportDiff.descriptionChanged && (
              <div className={styles.detailSection}>
                <h4 className={styles.detailTitle}>Description Changed</h4>
                <div className={styles.rowList}>
                  "{reportDiff.oldDescription || '(empty)'}" → "{reportDiff.newDescription || '(empty)'}"
                </div>
              </div>
            )}

            {/* コンテンツ（Markdown）の変更 */}
            {reportDiff.contentDiff.hasChanges && (
              <div className={styles.detailSection}>
                <h4 className={styles.detailTitle}>
                  Content Changes (+{reportDiff.contentDiff.addedLines} lines, -{reportDiff.contentDiff.removedLines} lines)
                </h4>
                <div className={styles.contentDiff}>
                  {reportDiff.contentDiff.lines.map((line: ContentLineDiff, idx: number) => (
                    <div
                      key={idx}
                      className={
                        line.type === 'added'
                          ? styles.lineAdded
                          : line.type === 'removed'
                            ? styles.lineRemoved
                            : styles.lineUnchanged
                      }
                    >
                      <span className={styles.lineNumber}>{line.lineNumber}</span>
                      <span className={styles.linePrefix}>
                        {line.type === 'added' ? '+' : line.type === 'removed' ? '-' : ' '}
                      </span>
                      <span className={styles.lineContent}>{line.content || ' '}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 要素の変更 */}
            {reportDiff.elementChanges.length > 0 && (
              <div className={styles.detailSection}>
                <h4 className={styles.detailTitle}>Element Changes</h4>
                <ul className={styles.schemaList}>
                  {reportDiff.elementChanges.map((change, idx) => (
                    <li key={idx}>
                      {change.type === 'added' && (
                        <span className={styles.schemaAdded}>
                          + {change.elementType}: "{change.elementName || change.elementId}"
                        </span>
                      )}
                      {change.type === 'removed' && (
                        <span className={styles.schemaRemoved}>
                          - {change.elementType}: "{change.elementName || change.elementId}"
                        </span>
                      )}
                      {change.type === 'modified' && (
                        <span className={styles.schemaModified}>
                          ~ {change.elementType}: "{change.elementName || change.elementId}"
                          {change.propertyChanges && change.propertyChanges.length > 0 && (
                            <span className={styles.propertyChanges}>
                              {' '}({change.propertyChanges.map(p => p.property).join(', ')})
                            </span>
                          )}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* 設定の変更 */}
            {reportDiff.settingsChanged && reportDiff.settingsChanges && reportDiff.settingsChanges.length > 0 && (
              <div className={styles.detailSection}>
                <h4 className={styles.detailTitle}>Settings Changes</h4>
                <ul className={styles.schemaList}>
                  {reportDiff.settingsChanges.map((change, idx) => (
                    <li key={idx}>
                      <span className={styles.schemaModified}>
                        {change.property}: {JSON.stringify(change.oldValue)} → {JSON.stringify(change.newValue)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // 列IDから列名を取得
  const getColumnName = (datasetId: string, columnId: string): string => {
    const dataset = project?.datasets[datasetId as DatasetId];
    if (!dataset) return columnId;
    const column = dataset.schema.columns.find((c: ColumnSchema) => c.id === columnId);
    return column?.name || columnId;
  };

  // 値をフォーマット
  const formatValue = (value: unknown): string => {
    if (value === null) return '(null)';
    if (value === undefined) return '(undefined)';
    if (typeof value === 'string') return value;
    if (typeof value === 'number') {
      if (Number.isNaN(value)) return 'NaN';
      return String(value);
    }
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    return String(value);
  };

  // 値が数値かどうかを判定
  const isNumericValue = (value: unknown): boolean => {
    return typeof value === 'number';
  };

  // 値セルのスタイルを取得
  const getValueCellStyle = (value: unknown, baseStyle: string): string => {
    if (isNumericValue(value)) {
      return `${styles.changeTableCellNumeric} ${baseStyle}`;
    }
    return `${styles.changeTableCell} ${baseStyle}`;
  };

  return (
    <div className={styles.container} data-testid="project-diff-content">
      <div className={styles.header}>
        <h2 className={styles.title}>Compare Project</h2>
        <p className={styles.description}>
          Compare the current project with another MDS file to see differences.
        </p>
      </div>

      <div className={styles.actions}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".mds"
          onChange={handleFileChange}
          style={{ display: 'none' }}
          data-testid="mds-file-input"
        />
        <button className={styles.selectButton} onClick={handleSelectFile}>
          Select MDS File to Compare...
        </button>
        <div className={styles.directionSelector}>
          <label htmlFor="compare-direction">Compare as:</label>
          <select
            id="compare-direction"
            className={styles.directionSelect}
            value={compareDirection}
            onChange={(e) => setCompareDirection(e.target.value as CompareDirection)}
            data-testid="compare-direction-select"
          >
            <option value="file-as-original">File → Current (show what changed)</option>
            <option value="current-as-original">Current → File (show file changes)</option>
          </select>
        </div>
      </div>

      {isLoading && (
        <div className={styles.loading}>Loading and comparing...</div>
      )}

      {errorMessage && (
        <div className={styles.error}>
          <strong>Error:</strong> {errorMessage}
        </div>
      )}

      {diffResult && (
        <div className={styles.results}>
          <div className={styles.comparedFile}>
            {compareDirection === 'file-as-original' ? (
              <>
                <strong>Original:</strong> {comparedFileName} → <strong>Modified:</strong> Current project
              </>
            ) : (
              <>
                <strong>Original:</strong> Current project → <strong>Modified:</strong> {comparedFileName}
              </>
            )}
          </div>

          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Summary</h3>
            {renderDiffSummary(diffResult.diff, diffResult.originalProject, diffResult.modifiedProject)}
          </div>

          {Object.keys(diffResult.diff.primaryDatasets.modified).length > 0 && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Modified Datasets (Details)</h3>
              <div className={styles.datasetList}>
                {Object.entries(diffResult.diff.primaryDatasets.modified).map(
                  ([datasetId, datasetDiff]) => renderDatasetDetails(datasetId, datasetDiff, diffResult.originalProject, diffResult.modifiedProject)
                )}
              </div>
            </div>
          )}

          {Object.keys(diffResult.diff.reports.modified).length > 0 && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Modified Reports (Details)</h3>
              <div className={styles.datasetList}>
                {Object.entries(diffResult.diff.reports.modified).map(
                  ([reportId, reportDiff]) => renderReportDetails(reportId, reportDiff)
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
